import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchableSelect from '../../components/SearchableSelect';
import {
  faSearch,
  faUser,
  faChartBar,
  faSpinner,
  faExclamationTriangle,
  faClipboardCheck,
  faSync,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import { useAuth } from '../../contexts/AuthContext';

const CatiProductivity = () => {
  const { user: currentUser } = useAuth();
  const [productivity, setProductivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedInterviewer, setSelectedInterviewer] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [selectedPost, setSelectedPost] = useState('');
  const [interviewers, setInterviewers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [posts, setPosts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    total_completes: 0,
    unique_interviewers: 0,
    unique_surveys: 0
  });

  useEffect(() => {
    fetchProductivity();
    fetchStats();
    fetchInterviewers();
    fetchSurveys();
    fetchPosts();
  }, [currentPage, dateFrom, dateTo, selectedInterviewer, selectedSurvey, selectedPost]);

  const fetchProductivity = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        date_from: dateFrom,
        date_to: dateTo,
        interviewer_id: selectedInterviewer,
        survey_id: selectedSurvey,
        post_id: selectedPost
      });

      const response = await api.get(`/cati/productivity?${params}`);
      
      if (response.data.success) {
        setProductivity(response.data.data.data || []);
        setPagination(response.data.data.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching productivity:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({
        interviewer_id: selectedInterviewer,
        survey_id: selectedSurvey,
        post_id: selectedPost,
        date_from: dateFrom,
        date_to: dateTo
      });

      const response = await api.get(`/cati/productivity/stats/simple?${params}`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchInterviewers = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSurvey) {
        params.append('survey_id', selectedSurvey);
      }
      const url = `/cati/interviewers${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      if (response.data.success) {
        // Convert object to array if needed
        const interviewersData = response.data.data || [];
        const interviewersArray = Array.isArray(interviewersData) ? interviewersData : Object.values(interviewersData);
        setInterviewers(interviewersArray);
      }
    } catch (error) {
      console.error('Error fetching interviewers:', error);
    }
  };

  const fetchSurveys = async () => {
    try {
      const response = await api.get('/cati/surveys');
      if (response.data.success) {
        // Convert object to array if needed
        const surveysData = response.data.data || [];
        const surveysArray = Array.isArray(surveysData) ? surveysData : Object.values(surveysData);
        setSurveys(surveysArray);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const params = new URLSearchParams();
      params.append('all', 'true');
      if (selectedSurvey) {
        params.append('survey_id', selectedSurvey);
      }
      const response = await api.get(`/cati/posts?${params.toString()}`);
      if (response.data.success) {
        // Handle paginated response - get the data array from pagination structure
        const postsData = response.data.data?.data || response.data.data || [];
        setPosts(Array.isArray(postsData) ? postsData : []);
      } else {
        console.error('Posts API returned success: false');
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
  };


  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'interviewer':
        setSelectedInterviewer(value);
        break;
      case 'survey':
        setSelectedSurvey(value);
        break;
      case 'post':
        setSelectedPost(value);
        break;
      case 'dateFrom':
        setDateFrom(value);
        break;
      case 'dateTo':
        setDateTo(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.post('/cati/productivity/refresh');
      
      if (response.data.success) {
        // Refresh the data after successful update
        await fetchProductivity();
        await fetchStats();
        setSuccessMessage('Données de productivité mises à jour avec succès!');
        setShowSuccessModal(true);
      } else {
        setSuccessMessage('Erreur lors de la mise à jour des données');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error refreshing productivity:', error);
      setSuccessMessage('Erreur lors de la mise à jour des données');
      setShowSuccessModal(true);
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">PRODUCTIVITÉ CATI</h1>
                <p className="text-gray-600">Suivi de la productivité des enquêteurs par poste et enquête</p>
              </div>
              {currentUser?.role === 'resp_cati' && (
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
                >
                  <FontAwesomeIcon
                    icon={faSync}
                    className={refreshing ? 'animate-spin' : ''}
                  />
                  {refreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div>
                <SearchableSelect
                  options={[{ id: '', title: 'Tous les postes' }, ...(Array.isArray(posts) ? posts.filter(p => p && p.id).map(p => ({ ...p, title: `Poste ${p.number}${p.label ? ` - ${p.label}` : ''}` })) : [])]}
                  selectedValue={selectedPost || ''}
                  onChange={(val) => handleFilterChange('post', val || '')}
                  placeholder="Tous les postes"
                  searchPlaceholder="Rechercher un poste..."
                  triggerClassName="px-4 py-2"
                  showOptionMeta={false}
                  getOptionLabel={(o) => o.title}
                />
              </div>
              <div>
                <SearchableSelect
                  options={[{ id: '', title: 'Tous les enquêteurs' }, ...(Array.isArray(interviewers) ? interviewers.filter(i => i && i.id).map(i => ({ ...i, title: i.name })) : [])]}
                  selectedValue={selectedInterviewer || ''}
                  onChange={(val) => handleFilterChange('interviewer', val || '')}
                  placeholder="Tous les enquêteurs"
                  searchPlaceholder="Rechercher un enquêteur..."
                  triggerClassName="px-4 py-2"
                  showOptionMeta={false}
                  getOptionLabel={(o) => o.title}
                />
              </div>
              <div>
                <SearchableSelect
                  options={[{ id: '', title: 'Toutes les enquêtes' }, ...(Array.isArray(surveys) ? surveys.filter(s => s && s.id) : [])]}
                  selectedValue={selectedSurvey || ''}
                  onChange={(val) => handleFilterChange('survey', val || '')}
                  placeholder="Toutes les enquêtes"
                  searchPlaceholder="Rechercher une enquête..."
                  triggerClassName="px-4 py-2"
                  showOptionMeta={false}
                  getOptionLabel={(o) => o.title}
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Date de début"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Date de fin"
                />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <FontAwesomeIcon icon={faChartBar} className="text-blue-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Complétions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_completes}</p>
                  <p className="text-xs text-gray-500">Surveys complétés</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <FontAwesomeIcon icon={faUser} className="text-green-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Enquêteurs Actifs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.unique_interviewers}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <FontAwesomeIcon icon={faClipboardCheck} className="text-yellow-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Enquêtes</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.unique_surveys}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Productivity Table */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                <p className="text-gray-600">Chargement de la productivité...</p>
              </div>
            ) : productivity.length === 0 ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600">Aucune donnée de productivité trouvée</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enquêteur
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enquête
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Poste
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Complétions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {productivity.map((item, index) => (
                        <tr key={`${item.for_date}-${item.interviewer_id}-${item.survey_id}-${item.post_id || 'no-post'}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatDate(item.for_date)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.interviewer?.name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.survey?.title || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.post ? item.post.number : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.completes || 0} complétés
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      {/* Mobile: Show page info */}
                      <div className="sm:hidden text-sm text-gray-700 text-center w-full">
                        Page {currentPage} sur {pagination.last_page}
                      </div>
                      
                      {/* Desktop: Show full info */}
                      <div className="hidden sm:block text-sm text-gray-700">
                        <span>
                          Affichage de <span className="font-medium">{pagination.from || 0}</span> à{' '}
                          <span className="font-medium">{pagination.to || 0}</span> sur{' '}
                          <span className="font-medium">{pagination.total || 0}</span> résultats
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                            let pageNum;
                            if (pagination.last_page <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= pagination.last_page - 2) {
                              pageNum = pagination.last_page - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-red-600 text-white border-red-600'
                                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === pagination.last_page}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
};

export default CatiProductivity;
